<template>
  <div class="challenge-container">
    <h1>🎄 Napraw lampki 🎄</h1>
    <p class="instructions">
      Uzyj CSS, aby naprawić lampki. Każda z nich wymaga innej właściwości.
    </p>

    <!-- Puzzle Progress -->
    <div class="progress">
      <button
        @click="prevPuzzle"
        :disabled="currentPuzzleIndex === 0"
        class="nav-btn"
      >
        ←
      </button>

      <div
        v-for="(puzzle, index) in puzzles"
        :key="index"
        class="progress-dot"
        :class="{
          completed: completedPuzzles.includes(index),
          active: currentPuzzleIndex === index,
        }"
      ></div>

      <button
        @click="nextPuzzle"
        :disabled="currentPuzzleIndex === puzzles.length - 1"
        class="nav-btn"
      >
        →
      </button>
    </div>

    <!-- Current Puzzle -->
    <div class="puzzle-card">
      <h2>{{ currentPuzzle.description }}</h2>

      <!-- Hint and Apply CSS buttons in one row -->
      <div class="button-row">
        <button @click="showHint = !showHint" class="hint-btn">
          {{ showHint ? "Schowaj wskazówkę" : "Pokaż wskazówkę" }}
        </button>
        <button @click="applyCSS" class="apply-btn">Zastosuj CSS</button>
      </div>

      <!-- Hint (conditionally shown) -->
      <div class="hint" v-if="showHint">
        {{ currentPuzzle.hint }}
      </div>

      <!-- CSS Input -->
      <textarea
        v-model="userCSS"
        placeholder="Wpisz swój CSS tutaj..."
        @keyup.enter="applyCSS"
      ></textarea>

      <!-- Tree with Dynamic Styling -->
      <div class="tree-container">
        <div
          class="tree"
          :style="[
            appliedStyle,
            completedPuzzles.includes(currentPuzzleIndex)
              ? currentPuzzle.correctStyle
              : {},
          ]"
        >
          💡
        </div>
      </div>

      <!-- Feedback -->
      <p v-if="feedback" class="feedback" :class="{ success: isCorrect }">
        {{ feedback }}
      </p>
    </div>

    <!-- Congratulations Modal -->
    <div v-if="allPuzzlesCompleted" class="congrats-modal">
      <div class="modal-content">
        <h2>🎉 Gratulacje! 🎉</h2>
        <p>Udało Ci się naprawić wszystkie lampki!</p>
        <button @click="claimReward" class="claim-btn">Powrót do menu</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import router from "@/router";
import { ref, computed } from "vue";

const userCSS = ref("");
const appliedStyle = ref({});
const feedback = ref("");
const isCorrect = ref(false);
const currentPuzzleIndex = ref(0);
const completedPuzzles = ref([]);
const showHint = ref(false);

const puzzles = [
  {
    description: "🎅: Światło jest czerwone, ale wolę zielony.",
    hint: "Użyj właściwości background (lub background-color), aby zmienić kolor na zielony.",
    requiredProperty: "background", // Może być "background" lub "background-color"
    correctStyle: { background: "green", fontSize: "48px" },
  },
  {
    description: "🎅: Ta lampka potrzebuje blasku!",
    hint: "Użyj właściwości box-shadow, aby dodać blask.",
    requiredProperty: "boxShadow", // Może być "box-shadow" w CSS
    correctStyle: {
      boxShadow: "0 0 15px 15px yellow",
      background: "yellow",
      fontSize: "48px",
    },
  },
  {
    description: "🎅: Ta lampka jest za mała! Zrób ją większą.",
    hint: "Użyj transform: scale, aby powiększyć lampkę.",
    requiredProperty: "transform", // Może być "transform" w CSS
    correctStyle: { transform: "scale(1.5)", fontSize: "48px" },
  },
];

const currentPuzzle = computed(() => puzzles[currentPuzzleIndex.value]);
const allPuzzlesCompleted = computed(
  () => completedPuzzles.value.length === puzzles.length
);

// Funkcja do konwersji CSS na camelCase (dla Vue)
const toCamelCase = (str) => {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Funkcja do sprawdzania, czy CSS jest poprawny i zawiera wymaganą właściwość
const applyCSS = () => {
  try {
    const styleObject = {};
    const rules = userCSS.value.split(";").filter((rule) => rule.trim());

    // Parsuj każdą regułę CSS
    for (const rule of rules) {
      const [key, value] = rule.split(":").map((s) => s.trim());
      if (key && value) {
        const jsKey = toCamelCase(key);
        styleObject[jsKey] = value;
      }
    }

    appliedStyle.value = styleObject;

    // Sprawdź, czy użytkownik użył wymaganej właściwości
    const requiredProperty = currentPuzzle.value.requiredProperty;
    const hasRequiredProperty = Object.keys(styleObject).some(
      (key) => toCamelCase(key) === requiredProperty
    );

    if (
      hasRequiredProperty &&
      !completedPuzzles.value.includes(currentPuzzleIndex.value)
    ) {
      completedPuzzles.value.push(currentPuzzleIndex.value);
      feedback.value = "✅ Super, lampka naprawiona!";
      isCorrect.value = true;
    } else if (!completedPuzzles.value.includes(currentPuzzleIndex.value)) {
      feedback.value = `❌ Coś jest nie tak. Możesz poprosić o wskazówkę.`;
      isCorrect.value = false;
    }
  } catch (error) {
    feedback.value = "⚠️ Nieprawidłowy CSS. Spróbuj ponownie!";
    isCorrect.value = false;
  }
};

const nextPuzzle = () => {
  showHint.value = false;
  if (currentPuzzleIndex.value < puzzles.length - 1) {
    currentPuzzleIndex.value++;
    resetPuzzle();
  }
};

const prevPuzzle = () => {
  showHint.value = false;
  if (currentPuzzleIndex.value > 0) {
    currentPuzzleIndex.value--;
    resetPuzzle();
  }
};

const resetPuzzle = () => {
  showHint.value = false;
  userCSS.value = "";
  feedback.value = "";
  isCorrect.value = false;
  appliedStyle.value = {};
};

const claimReward = () => {
  document.cookie = `puzzle3=completed; max-age=3600`;
  router.push("/home");
};
</script>

<style scoped>
.challenge-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

h1 {
  color: #d32f2f;
  margin-bottom: 10px;
  text-align: center;
}

.instructions {
  color: #5d4037;
  margin-bottom: 20px;
  text-align: center;
}

.progress {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: 20px 0;
}

.progress-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #bdbdbd;
}

.progress-dot.completed {
  background: #4caf50;
}

.progress-dot.active {
  border: 2px solid #d32f2f;
  margin-top: -2px;
}

.puzzle-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.puzzle-card h2 {
  margin-top: 0;
  color: #333;
}

/* Button row container */
.button-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

/* Buttons in the same row */
.hint-btn,
.apply-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.hint-btn {
  background: #ff9800;
  color: white;
}

.hint-btn:hover {
  background: #e68a00;
}

.apply-btn {
  background: #44c932;
  color: white;
}

.apply-btn:hover {
  background: #3bb71c;
}

textarea {
  width: 100%;
  padding: 10px;
  margin: 10px 0;
  border: 2px solid #d32f2f;
  border-radius: 5px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  box-sizing: border-box;
}

.tree-container {
  margin: 20px 0;
  display: flex;
  justify-content: center;
}

.tree {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #b86464;
  border-radius: 50%;
  margin: 0 auto;
  transition: all 0.3s;
}

.feedback {
  font-weight: bold;
  margin: 10px 0;
  text-align: center;
}

.feedback.success {
  color: #4caf50;
}

.nav-btn {
  background: #d32f2f;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  width: 15%;
}

.nav-btn:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
  pointer-events: none;
}

.congrats-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  max-width: 400px;
  width: 90%;
}

.claim-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 20px;
}

.hint {
  background: #fff3e0;
  color: #e68a00;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 10px;
}
</style>
